import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { PhoneIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { ValueStub } from 'constants/data';
import { formatPhoneNumberForDisplay, toDialableNumber } from 'utils/phone';

// Direct module imports, not the store barrels: this renders inside CDS primitives, and pulling
// the whole system actions/selectors graph in there trips circular-init on the reselect creators.
import { makeOutgoingCallAction } from 'store/system/actions/telephony.actions';
import { selectCanPlaceCall } from 'store/system/selectors/telephony.selectors';

import { CALL_TOOLTIP_PREFIX, ClickablePhoneVariant } from './ClickablePhone.constants';
import { callIconButtonStyles, callIconStyles, callLinkStyles } from './ClickablePhone.styles';
import { ClickablePhoneTestIds } from './ClickablePhone.testIds';

/**
 * Renders a phone number in the canonical display format, turning it into a dial affordance for
 * users entitled to place calls — but only for a value that resolves to a dialable number, so an
 * unrecognized shape stays plain text instead of offering a call that would reach someone else.
 * `applicationId` / `contactId` ride along on the call so telephony can attribute it to the loan
 * or CRM contact the number was dialed from.
 */
const ClickablePhone = ({
  value,
  applicationId = null,
  contactId = null,
  variant = ClickablePhoneVariant.LINK,
  typographyVariant = 'inherit',
  emptyFallback = ValueStub.COMMON,
}) => {
  const dispatch = useDispatch();
  const canPlaceCall = useSelector(selectCanPlaceCall);

  const formattedValue = formatPhoneNumberForDisplay(value);
  const dialableNumber = toDialableNumber(value);
  const tooltipTitle = `${CALL_TOOLTIP_PREFIX} ${formattedValue}`;

  const onCall = useCallback(
    event => {
      event.stopPropagation();
      event.preventDefault();

      dispatch(
        makeOutgoingCallAction({
          destinationNumber: dialableNumber,
          applicationId,
          contactId,
        }),
      );
    },
    [applicationId, contactId, dispatch, dialableNumber],
  );

  if (!value) {
    return (
      <Typography component="span" variant={typographyVariant}>
        {emptyFallback}
      </Typography>
    );
  }

  if (!canPlaceCall || !dialableNumber) {
    return (
      <Typography component="span" variant={typographyVariant}>
        {formattedValue}
      </Typography>
    );
  }

  if (variant === ClickablePhoneVariant.ICON_BUTTON) {
    return (
      <Tooltip title={tooltipTitle}>
        <IconButton
          aria-label={tooltipTitle}
          data-testid={ClickablePhoneTestIds.CALL_BUTTON}
          size="small"
          sx={callIconButtonStyles}
          onClick={onCall}
        >
          <PhoneIcon size={16} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipTitle}>
      <Box
        aria-label={tooltipTitle}
        component="button"
        data-testid={ClickablePhoneTestIds.CALL_BUTTON}
        sx={callLinkStyles}
        type="button"
        onClick={onCall}
      >
        <Box component="span" sx={callIconStyles}>
          <PhoneIcon size={14} />
        </Box>
        <Typography
          component="span"
          data-testid={ClickablePhoneTestIds.VALUE_LABEL}
          variant={typographyVariant}
        >
          {formattedValue}
        </Typography>
      </Box>
    </Tooltip>
  );
};

ClickablePhone.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  applicationId: PropTypes.string,
  contactId: PropTypes.string,
  variant: PropTypes.oneOf(Object.values(ClickablePhoneVariant)),
  typographyVariant: PropTypes.string,
  emptyFallback: PropTypes.node,
};

export default ClickablePhone;
